import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { FileEdit, Clock, CheckCircle2, XCircle } from "lucide-react";

const statusBadgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      status: {
        draft:
          "border-gray-500/20 bg-gray-500/10 text-gray-700 hover:bg-gray-500/20",
        pending:
          "border-amber-500/20 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20",
        sent: "border-green-500/20 bg-green-500/10 text-green-700 hover:bg-green-500/20",
        failed:
          "border-red-500/20 bg-red-500/10 text-red-700 hover:bg-red-500/20",
      },
    },
    defaultVariants: {
      status: "draft",
    },
  },
);

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusBadgeVariants> {
  showIcon?: boolean;
}

const statusConfig = {
  draft: {
    icon: FileEdit,
    label: "DRAFT",
  },
  pending: {
    icon: Clock,
    label: "PENDING",
  },
  sent: {
    icon: CheckCircle2,
    label: "SENT",
  },
  failed: {
    icon: XCircle,
    label: "FAILED",
  },
};

function StatusBadge({
  className,
  status,
  showIcon = true,
  ...props
}: StatusBadgeProps) {
  const config = statusConfig[status || "draft"];
  const IconComponent = config.icon;

  return (
    <div className={cn(statusBadgeVariants({ status }), className)} {...props}>
      {showIcon && <IconComponent className="h-3 w-3" />}
      <span>{config.label}</span>
    </div>
  );
}

export { StatusBadge, statusBadgeVariants };
